import { useEffect, useState, useMemo } from "react";
import { useGetService } from "./useGetService";
import { API } from "../constants";

// Third party
import { usePostService } from "./usePostService";
import { toast } from "react-toastify";
import moment from "moment";

export const useUnlistedData = (
    startDate: Date,
    endDate: Date,
    selectedTab: string
) => {
    const { postData } = usePostService();

    const [rows, setRows] = useState<any[]>([]);
    const [isRowsLoading, setRowsLoading] = useState(false);
    const [refreshingText, setRefreshingText] = useState<string>("");

    // Initial URL - computed once on mount, not reactive to startDate/endDate changes
    // This prevents automatic API calls when dates change
    const initialUrl = useMemo(() => {
        const assignmentStartDate = moment(startDate).startOf('day');
        const assignmentEndDate = moment(endDate).endOf('day');
        return `${API.UNLISTED_TM_SCANS.GET_UNLISTED_TM_SCANS
            }?startDate=${assignmentStartDate.toISOString()}&endDate=${assignmentEndDate.toISOString()}`;
    }, []); // Empty dependency array - only computed once on mount

    // FETCH ASSIGNMENT APPROVALS
    // Use initial URL for first load, then refetch manually via refetchData()
    const {
        response: assignmentApprovalsResponse,
        isLoading: isAssignmentApprovalsLoading,
        refetch: refetchAssignmentApprovals,
        error,
        detailedError
    } = useGetService(initialUrl);

    const [allRows, setAllRows] = useState<any[]>([]);

    useEffect(() => {
        setRowsLoading(isAssignmentApprovalsLoading);

        if (
            !isAssignmentApprovalsLoading &&
            assignmentApprovalsResponse?.data &&
            Array.isArray(assignmentApprovalsResponse.data)
        ) {
            // Store all rows for count calculation (excluding items without id)
            const rowsWithId = assignmentApprovalsResponse.data.filter(
                (item: any) => item.id
            );
            setAllRows(rowsWithId);

            // Filter rows based on selectedTab (approvalStatus)
            const filteredRows = rowsWithId.filter(
                (item: any) => item.approvalStatus === selectedTab
            );
            setRows(filteredRows);
        } else if (!isAssignmentApprovalsLoading) {
            setAllRows([]);
            setRows([]);
        }
    }, [assignmentApprovalsResponse, isAssignmentApprovalsLoading, selectedTab]);

    const refetchData = () => {
        // Use current startDate and endDate directly (the parameters passed to the hook)
        // This ensures we always use the latest values, not the cached URL
        const currentStartDate = moment(startDate).startOf('day');
        const currentEndDate = moment(endDate).endOf('day');
        const url = `${API.UNLISTED_TM_SCANS.GET_UNLISTED_TM_SCANS
            }?startDate=${currentStartDate.toISOString()}&endDate=${currentEndDate.toISOString()}`;
        refetchAssignmentApprovals(url);
    };

    // Auto-refresh every 5 minutes using current startDate and endDate
    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshingText("Refreshing data...");
            const currentStartDate = moment(startDate).startOf('day');
            const currentEndDate = moment(endDate).endOf('day');
            const url = `${API.UNLISTED_TM_SCANS.GET_UNLISTED_TM_SCANS
                }?startDate=${currentStartDate.toISOString()}&endDate=${currentEndDate.toISOString()}`;
            refetchAssignmentApprovals(url).finally(() => {
                setRefreshingText("");
            });
        }, 5 * 60 * 1000); // 5 minutes in milliseconds

        return () => clearInterval(interval);
    }, [startDate, endDate, refetchAssignmentApprovals]);

    const handleApprove = async (approvals: { id: string; createdOnISO: string }[], comment?: string) => {
        const payload = {
            approvalStatus: "APPROVED",
            notes: comment || "Approved",
            ids: approvals,
        };

        const resp = await postData(
            `${API.UNLISTED_TM_SCANS.ASSIGNMENT_APPROVAL}/approve`,
            payload,
        );

        return resp;
    };

    const handleReject = async (approvals: { id: string; createdOnISO: string }[], comment?: string) => {
        const payload = {
            approvalStatus: "REJECTED",
            notes: comment || "Rejected",
            ids: approvals,
        };

        const resp = await postData(
            `${API.UNLISTED_TM_SCANS.ASSIGNMENT_APPROVAL}/approve`,
            payload,
        );
        return resp;
    };

    const handleArriveClickedCall = async (payload: any) => {
        try {
            const resp = await postData(
                API.CARNIVAL_ADMIN.CREATE_SERVICE_REQUEST,
                payload
            );

            toast.success("Acknowledged successfully by shipboard! ");
            const dataIndex = rows.findIndex(
                (x: any) => x.EmpId === payload.assignedDetails?.[0]?.tmId
            );

            if (dataIndex === -1) {
                toast.error("TM not found in the list!");
                return;
            }

            const currentData = [...rows] as any;
            currentData[dataIndex].isArrivedToShip = true;
            currentData[dataIndex].assignedInfo = Array.isArray(resp.data)
                ? resp.data
                : [];
            setRows(currentData);
        } catch (err) {
            console.error(err);
            toast.error("Failed!");
        }
    };


    return {
        rows,
        allRows, // Return all rows for count calculation
        isRowsLoading,
        refetchData,
        handleApprove,
        handleReject,
        handleArriveClickedCall,
        refreshingText,
        error,
        detailedError,
    };
};
